"use client";

import React, { useEffect } from "react";
import Table from "@core/components/table";
import TablePagination from "@core/components/table/pagination";
import TableFooter from "@core/components/table/footer";
import { useTanStackTable } from "@core/components/table/custom/use-TanStack-Table";
import Filters from "./filters";
import { exportToCSV } from "@core/utils/export-to-csv";
import { AllUsersListColumn } from "./columns";
import type { User } from "packages/app/src/types";

interface AllUsersTableProps {
  users: User[];
  isLoading: boolean;
  error: Error | null;
}

export default function AllUsersTable({
  users,
  isLoading,
  error,
}: AllUsersTableProps) {
  if (isLoading) {
    return (
      <div className="w-full h-60 flex justify-center items-center text-[16px] font-semibold">
        Loading users data
      </div>
    );
  }
  // if there was an error fetching, show it
  if (error) {
    return (
      <div className="text-red-600 p-4">
        Error loading users data: {error.message}
      </div>
    );
  }
  
  const { table, setData } = useTanStackTable<User>({
    tableData: users,
    columnConfig: AllUsersListColumn,
    options: {
      initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
      meta: {
        handleDeleteRow: (row) => {
          setData((prev) => prev.filter((u) => u._id !== row._id));
        },
        handleMultipleDelete: (rows) => {
          setData((prev) => prev.filter((u) => !rows.includes(u)));
        },
        updateRow: (rowIndex, patch) =>
          setData((prev) => {
            const copy = [...prev];
            copy[rowIndex] = { ...copy[rowIndex], ...patch };
            return copy;
          }),
      },
      enableColumnResizing: false,
    },
  });

  // React.useEffect(() => {
  //   setData(users);
  // }, [users, setData]);
  // if the parent says we’re loading, show a spinner

  const selected = table.getSelectedRowModel().rows.map((r) => r.original);

  function handleExport() {
    exportToCSV(
      selected,
      "ID,FirstName,LastName,Email,KYC,Verified,Joined",
      `users_export_${selected.length}`
    );
  }

  useEffect(() => {
    table.setColumnOrder([
      "userId",
      "fullName",
      "email",
      "phoneNumber",
      "kycVerified",
      "verified",
      "createdAt",
      "isSuspended",
      "actions",
    ]);
  }, [table]);

  return (
    <>
      <Filters table={table} />
      <Table
        table={table}
        variant="modern"
        classNames={{
          container: "border border-muted rounded-md",
          rowClassName: "last:border-0",
        }}
      />
      <TableFooter table={table} onExport={handleExport} />
      <TablePagination table={table} className="py-4" />
    </>
  );
}
